import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import toast from "react-hot-toast";
import { baselineService } from "../services/baselineService";
import { getStoredClientId } from "../utils/tokenUtils";

const BaselineFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = null,
  projectId,
  clientId,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    version: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          name: initialData.name || "",
          version: initialData.version || "",
          description: initialData.description || "",
        });
      } else {
        setFormData({
          name: "",
          version: "",
          description: "",
        });
      }
      setErrors({});
    }
  }, [isOpen, initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate
      const newErrors = {};
      if (!formData.name.trim()) {
        newErrors.name = "Baseline name is required";
      }
      if (!formData.version.trim()) {
        newErrors.version = "Version is required";
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setLoading(false);
        return;
      }

      const effectiveClientId = clientId || getStoredClientId();

      if (initialData) {
        // Update existing baseline (only if draft)
        if (initialData.status === "published") {
          toast.error(
            "Published baselines cannot be edited. Create a branch to make changes.",
          );
          setLoading(false);
          return;
        }

        await baselineService.updateBaseline(
          initialData.baseline_id,
          projectId,
          effectiveClientId,
          {
            name: formData.name.trim(),
            version: formData.version.trim(),
            description: formData.description.trim() || null,
          },
        );
        toast.success("Baseline updated successfully!");
      } else {
        // Create new baseline
        await baselineService.createBaseline({
          project_id: projectId,
          client_id: effectiveClientId,
          name: formData.name.trim(),
          version: formData.version.trim(),
          description: formData.description.trim() || null,
        });
        toast.success("Baseline created successfully!");
      }

      if (onSubmit) {
        await onSubmit();
      }
      onClose();
    } catch (error) {
      console.error("Error saving baseline:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to save baseline";
      toast.error(errorMessage);
      if (error.response?.data?.code === "DUPLICATE_BASELINE_VERSION") {
        setErrors({ version: "A baseline with this version already exists" });
      }
      if (error.response?.data?.code === "BASELINE_IMMUTABLE") {
        toast.error("Published baselines cannot be edited.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isPublished = initialData?.status === "published";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {initialData ? "Edit Baseline" : "Create New Baseline"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {isPublished && (
          <div className="p-4 bg-yellow-50 border-b border-yellow-200">
            <p className="text-sm text-yellow-800">
              This baseline is published and cannot be edited. Create a branch
              to make changes.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label htmlFor="name" className="text-gray-700">
              Baseline Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={loading || isPublished}
              className={`mt-1 ${errors.name ? "border-red-300" : ""}`}
              placeholder="Enter baseline name"
              required
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          <div>
            <Label htmlFor="version" className="text-gray-700">
              Version <span className="text-red-500">*</span>
            </Label>
            <Input
              id="version"
              name="version"
              value={formData.version}
              onChange={handleChange}
              disabled={loading || isPublished}
              className={`mt-1 ${errors.version ? "border-red-300" : ""}`}
              placeholder="e.g., 1.0, 2.1"
              required
            />
            {errors.version && (
              <p className="mt-1 text-sm text-red-600">{errors.version}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Version must be unique within the project
            </p>
          </div>

          <div>
            <Label htmlFor="description" className="text-gray-700">
              Description
            </Label>
            <Textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              disabled={loading || isPublished}
              rows={3}
              className="mt-1"
              placeholder="Enter baseline description (optional)"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              onClick={onClose}
              disabled={loading}
              variant="outline"
            >
              Cancel
            </Button>
            {!isPublished && (
              <Button type="submit" disabled={loading}>
                {loading
                  ? "Saving..."
                  : initialData
                    ? "Update Baseline"
                    : "Create Baseline"}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default BaselineFormModal;
